import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Clock,
  User,
  MapPin
} from 'lucide-react';
import MealsList from './MealsList';
import ScheduleActions from './ScheduleActions';

const ScheduleCard = ({ 
  schedule, 
  statusConfig, 
  handleMarkReady, 
  setSelectedSchedule, 
  updateStatusMutation,
  formatTimeRange 
}) => {
  const { i18n, t } = useTranslation();
  
  const statusInfo = statusConfig[schedule.status] || statusConfig.SCHEDULED;
  const StatusIcon = statusInfo.icon;

  // Determine if this is a pending incomplete meal selection
  const isPendingIncomplete = schedule.status === 'PENDING';
  const isScheduledReady = schedule.status === 'SCHEDULED';

  // Open Google Maps location in a new tab
  const openGoogleMaps = () => {
    if (schedule.location) {
      let googleMapsUrl;
      
      // If coordinates are available, use them for precise location
      if (schedule.location.latitude && schedule.location.longitude) {
        googleMapsUrl = `https://www.google.com/maps?q=${schedule.location.latitude},${schedule.location.longitude}`;
      } else {
        // Build a comprehensive address for better search results
        const addressParts = [
          schedule.location.name,
          schedule.location.buildingName,
          schedule.location.buildingNumber,
          schedule.location.streetName,
          schedule.location.area
        ].filter(Boolean); // Remove null/undefined values
        
        const locationText = addressParts.join(', ');
        googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationText)}`;
      }
      
      // Open Google Maps in a new tab
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
      <div className="p-4">
        <div className="flex flex-col space-y-3">
          {/* Header with Status */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                {schedule.subscriptionName}
              </h3>
              <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTimeRange(schedule.timeToServeFrom, schedule.timeToServeTo, i18n.language)}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {schedule.servingDate}
                </span>
              </div>
            </div>
            
            <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full border text-xs font-medium ${statusInfo.color}`}>
              <StatusIcon className="w-3 h-3" />
              {statusInfo.label}
            </div>
          </div>

          {/* Meals - Simplified */}
          <MealsList meals={schedule.meals} i18n={i18n} compact={true} />

          {/* Status Messages - More Compact */}
          {isPendingIncomplete && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-2">
              <p className="text-xs font-medium text-amber-800">
                {t('customerMeals.pendingIncomplete', 'Incomplete Meal Selection')}
              </p>
            </div>
          )}

          {isScheduledReady && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
              <p className="text-xs font-medium text-blue-800">
                {t('customerMeals.scheduledReady', 'Ready for Preparation')}
              </p>
            </div>
          )}

          {/* Location with Google Maps Button */}
          {schedule.location && (
            <div className="bg-gray-50 rounded-md p-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 mb-1">
                    {t('deliveryLocation', 'Delivery Location')}
                  </p>
                  <div className="text-xs text-gray-600 space-y-1">
                    {/* Building/Location Name */}
                    <p className="truncate">
                      {schedule.location.name}
                      {schedule.location.buildingName && `, ${schedule.location.buildingName}`}
                    </p>
                    
                    {/* Address Details */}
                    {(schedule.location.streetName || schedule.location.area) && (
                      <p className="truncate">
                        {schedule.location.buildingNumber && `${schedule.location.buildingNumber} `}
                        {schedule.location.streetName}
                        {schedule.location.streetName && schedule.location.area && ', '}
                        {schedule.location.area}
                      </p>
                    )}
                    
                    {/* Apartment Number */}
                    {schedule.location.apartmentNumber && (
                      <p className="truncate">
                        {t('apartment', 'Apt')}: {schedule.location.apartmentNumber}
                      </p>
                    )}
                    
                    {/* Note if available */}
                    {schedule.location.note && (
                      <p className="truncate text-gray-500 italic">
                        {schedule.location.note}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={openGoogleMaps}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded border border-blue-200 hover:border-blue-300 transition-colors"
                  title={t('openGoogleMaps', 'Open in Google Maps')}
                >
                  <MapPin className="w-3 h-3" />
                  {t('googleMaps', 'Google Maps')}
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <ScheduleActions
            schedule={schedule}
            statusInfo={statusInfo}
            handleMarkReady={handleMarkReady}
            setSelectedSchedule={setSelectedSchedule}
            updateStatusMutation={updateStatusMutation}
            compact={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ScheduleCard;
